import { useMemo, useState } from "react";
import { type GroupedOptions } from "@root/shared/components/SearchableSelectBox";

export type WorkCategory = "求職者とマッチする求人" | "その他の求人";
export type EmptyCategory = "";

export type Work = {
  value: string;
  label: string;
  brandName: string;
  businessType: string;
  occupation: string;
  category: WorkCategory | EmptyCategory;
  shops: readonly {
    name: string;
    prefecture: string;
  }[];
};

export type WorkOption = {
  label: string;
  value: string;
  category: WorkCategory | EmptyCategory;
  shops: readonly string[];
};

export type WorkGroupedOptions = GroupedOptions<WorkOption>;

export type SelectedConditions = {
  brands: string | undefined;
  prefectures: string | undefined;
  businessTypes: string | undefined;
  occupations: string | undefined;
};

export const useWorksFilter = (
  works: readonly Work[],
  currentWorkId: string | undefined,
  onWorkChange: (workId: string | undefined) => void,
) => {
  const [selectedConditions, setSelectedConditions] = useState<SelectedConditions>({
    brands: undefined,
    prefectures: undefined,
    businessTypes: undefined,
    occupations: undefined,
  });
  const [isVisible, setIsVisible] = useState(false);

  const matched = filterWorksByConditions(works, selectedConditions);
  const selectableWorks = mapToSelectableWorks(matched);

  const hasCategory = useMemo(() => selectableWorks.some(hasWorkCategory), [selectableWorks]);
  const workOptions = useMemo(
    () => (hasCategory ? createGroupedOptions(selectableWorks) : selectableWorks),
    [hasCategory, selectableWorks],
  );

  const brandOptions = useMemo(
    () => generateOptions(works.map(({ brandName }) => brandName)),
    [works],
  );
  const prefectureOptions = useMemo(
    () => generateOptions(works.flatMap(({ shops }) => shops.map(({ prefecture }) => prefecture))),
    [works],
  );
  const businessTypeOptions = useMemo(
    () => generateOptions(works.map(({ businessType }) => businessType)),
    [works],
  );
  const occupationOptions = useMemo(
    () => generateOptions(works.map(({ occupation }) => occupation)),
    [works],
  );

  const handleChangeCondition = (value: string, field: keyof SelectedConditions) => {
    const newConditions = {
      ...selectedConditions,
      [field]: value || undefined,
    };
    const newMatched = filterWorksByConditions(works, newConditions);
    const newSelectableWorks = mapToSelectableWorks(newMatched);

    setSelectedConditions(newConditions);

    // 絞り込み後の候補に現在選択している求人が存在しない場合、選択を解除する
    if (
      currentWorkId &&
      currentWorkId !== "auto" &&
      !newSelectableWorks.some((work) => work.value === currentWorkId)
    ) {
      onWorkChange(undefined);
    }
  };

  const handleToggleFilter = () => {
    setIsVisible((prev) => !prev);
  };

  return {
    selectableWorksCount: selectableWorks.length,
    workOptions,
    brandOptions,
    prefectureOptions,
    businessTypeOptions,
    occupationOptions,
    isVisible,
    handleChangeCondition,
    handleToggleFilter,
  };
};

// 求人を、指定された条件に一致するもののみに絞り込む
const filterWorksByConditions = (works: readonly Work[], conditions: SelectedConditions) => {
  const { brands, businessTypes, occupations, prefectures } = conditions;
  return works.filter((work) => {
    const matchesBrand = !brands || brands === work.brandName;
    const matchesBusinessType = !businessTypes || businessTypes === work.businessType;
    const matchesOccupation = !occupations || occupations === work.occupation;
    const matchesPrefecture =
      !prefectures || work.shops.some((shop) => shop.prefecture === prefectures);
    return matchesBrand && matchesBusinessType && matchesOccupation && matchesPrefecture;
  });
};

// 絞り込み条件などを含む求人リストから、求人選択プルダウンに必要な情報のみを持つ求人リストを作る
const mapToSelectableWorks = (works: readonly Work[]) =>
  works.map((work) => ({
    label: work.label,
    value: work.value,
    category: work.category,
    shops: work.shops.map(({ name }) => name),
  }));

const generateOptions = (options: string[]) => {
  return Array.from(new Set(options)).map((optionName) => ({
    label: optionName,
    value: optionName,
  }));
};

const MATCHED_WORKS_CATEGORY_NAME = "求職者とマッチする求人";
const OTHER_WORKS_CATEGORY_NAME = "その他の求人";

const createGroupedOptions = (works: readonly WorkOption[]): WorkGroupedOptions[] => {
  const grouped = [
    {
      label: MATCHED_WORKS_CATEGORY_NAME,
      options: works.filter((work) => work.category === MATCHED_WORKS_CATEGORY_NAME),
    },
    {
      label: OTHER_WORKS_CATEGORY_NAME,
      options: works.filter((work) => work.category === OTHER_WORKS_CATEGORY_NAME),
    },
  ];

  return grouped;
};

const hasWorkCategory = (work: {
  category: WorkCategory | EmptyCategory;
}): work is { category: WorkCategory } => {
  return work.category !== "";
};
